import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, FileText, Lock, Percent, Plus, RefreshCw, Tag, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import {
  cashReceipts as cashReceiptsApi,
  fees as feesApi,
  orders as ordersApi,
  settings as settingsApi,
  unmatchedPayments as unmatchedApi,
} from '@/api/endpoints'
import { Alert, HelpTip, Modal, Select, Spinner } from '@/components/ui'
import { usePlanFeature } from '@/features/billing/PlanFeature'
import { hasBankImports } from '@/features/payments/BankImports'
import { useAuth } from '@/store/auth'
import { useBootstrap } from '@/hooks/useBootstrap'
import { cn, currencySymbol, formatTime, getActiveLocale, parseNaive, formatAmount, formatNumber } from '@/lib/utils'
import type {
  ClientFee,
  ClientReservation,
  DiscountType,
  OrderDetail,
  OrderDocument,
  Payment,
} from '@/types/api'
import { PaymentType } from '@/types/api'
import type { MissingTariff } from '@/types/api'

import { confirmRemovePayment } from './confirmRemovePayment'
import MissingPriceDialog from './MissingPriceDialog'
import { DocumentLink, InvoiceButtons, ProviderFee, SendDocumentsButton, documentOf } from './OrderDocuments'

interface Props {
  reservation: ClientReservation
  clientName: string
  readOnly?: boolean
  onClose: () => void
}

/** Spôsoby úhrady, ktoré sa dajú zapísať. „Nezaplatené“ nie je platba. */
const PAYMENT_TYPES = [PaymentType.Cash, PaymentType.Card, PaymentType.Transfer, PaymentType.Invoice, PaymentType.Other]

/**
 * „Detaily platby od zákazníka“ — nad celou objednávkou, nie nad lekciou.
 *
 * Jednotkou je **variabilný symbol**: kurz na päť dní zákazník objednáva
 * a platí naraz, takže okno ukazuje všetky jeho termíny, všetky položky, čo
 * z toho už prišlo a čo zostáva. Zapísaná úhrada sa preto prejaví na všetkých
 * lekciách objednávky - to je celý dôvod, prečo symbol existuje.
 *
 * Lekcia bez symbolu (prevzaté dáta, ktoré backfill ešte neošetril) sa
 * zobrazí po starom, len so svojimi položkami: rozbité okno je horšie než
 * okno bez platobnej časti.
 */
export default function PaymentDetailsModal({
  reservation,
  clientName,
  readOnly,
  onClose,
}: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const symbol = currencySymbol()
  // Škola eviduje platby inde - okno ukazuje len položky a cenu, nie platby.
  const simplePayments = Boolean(useBootstrap().data?.settings.simple_payments)
  const ref = reservation.payment_ref ?? null

  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [target, setTarget] = useState<number>(reservation.id)
  const [error, setError] = useState<string | null>(null)

  const order = useQuery({
    queryKey: ['order', ref],
    queryFn: () => ordersApi.get(ref as number),
    enabled: ref !== null,
  })

  // Bez symbolu sa dá pýtať len na položky jednej lekcie.
  const loose = useQuery({
    queryKey: ['fees', reservation.id],
    queryFn: () => feesApi.list({ client_reservation_id: reservation.id, limit: 100 }),
    enabled: ref === null,
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['order'] })
    void qc.invalidateQueries({ queryKey: ['fees'] })
    void qc.invalidateQueries({ queryKey: ['calendar'] })
    void qc.invalidateQueries({ queryKey: ['client-credit'] })
    void qc.invalidateQueries({ queryKey: ['client-money'] })
  }
  const onError = (err: unknown) => setError(apiErrorMessage(err))

  const addFee = useMutation({
    mutationFn: () =>
      feesApi.addCustom({
        client_reservation_id: target,
        amount: amount.replace(',', '.'),
        description: description.trim(),
      }),
    onSuccess: () => {
      setDescription('')
      setAmount('')
      setError(null)
      invalidate()
    },
    onError,
  })

  const updateFee = useMutation({
    mutationFn: ({ id, ...payload }: { id: number } & Parameters<typeof feesApi.update>[1]) =>
      feesApi.update(id, payload),
    onSuccess: () => {
      setError(null)
      invalidate()
    },
    onError,
  })

  const removeFee = useMutation({
    mutationFn: (id: number) => feesApi.remove(id),
    onSuccess: invalidate,
    onError,
  })

  const totalDiscount = useMutation({
    mutationFn: (payload: {
      discount_type?: DiscountType | null
      discount_value?: string | number | null
    }) => feesApi.setTotalDiscount({ client_reservation_id: target, ...payload }),
    onSuccess: () => {
      setError(null)
      invalidate()
    },
    onError,
  })

  const recalculate = useMutation({
    mutationFn: async () => {
      const ids = lessons.length ? lessons.map((l) => l.id) : [reservation.id]
      for (const id of ids) await feesApi.recalculate({ client_reservation_id: id })
    },
    onSuccess: invalidate,
    onError,
  })

  const isLoading = ref === null ? loose.isLoading : order.isLoading
  const data: OrderDetail | null = ref === null ? null : (order.data ?? null)
  const allRows: ClientFee[] = data ? data.fees : (loose.data?.results ?? [])
  const lessons = data?.lessons ?? [reservation]

  // Cieľová lekcia pre nové položky a zľavu. Prednastavená je tá, z ktorej sa
  // okno otvorilo - operátor najčastejšie dopisuje k nej.
  useEffect(() => {
    if (!lessons.some((l) => l.id === target)) setTarget(lessons[0]?.id ?? reservation.id)
  }, [lessons, target, reservation.id])

  // Lekcie, pre ktoré cenník nemá sadzbu - správca ju môže doplniť hneď tu.
  const canEditPricing = Boolean(useAuth((s) => s.user)?.can_administrate)
  const lessonIds = lessons.map((l) => l.id).filter(Boolean)
  const missing = useQuery({
    queryKey: ['pricing-missing', lessonIds, allRows.length],
    queryFn: () => settingsApi.missingPricing(lessonIds),
    enabled: !isLoading && lessonIds.length > 0,
  })
  const missingVariants = groupMissing(missing.data ?? [])
  const [adding, setAdding] = useState<{ tariff: MissingTariff; ids: number[] } | null>(null)

  const rows = allRows.filter((f) => !f.is_total_discount)
  const discountRow = allRows.find(
    (f) => f.is_total_discount && f.client_reservation_id === target,
  ) ?? null

  // Medzisúčet zľavy sa počíta z položiek **tej istej** lekcie, inak by sa
  // percentuálna zľava tvárila, že ide z celej objednávky.
  const subtotal = rows
    .filter((f) => f.client_reservation_id === target)
    .reduce((s, f) => s + Number.parseFloat(f.amount || '0'), 0)
  const total = data
    ? Number.parseFloat(data.total)
    : allRows.reduce((s, f) => s + Number.parseFloat(f.amount || '0'), 0)
  const paid = data ? Number.parseFloat(data.paid) : 0
  const due = data ? Number.parseFloat(data.due) : 0

  const parsedAmount = Number(amount.replace(',', '.'))
  const canAdd = amount.trim() !== '' && !Number.isNaN(parsedAmount) && parsedAmount >= 0

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`${clientName} — ${t('payments.title')}`}
    >
      <div className="space-y-2">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <OrderHeader
          paymentRef={ref}
          lessons={lessons}
          total={total}
          paid={paid}
          due={due}
          overpaid={data ? Number.parseFloat(data.overpaid) : 0}
          settled={Boolean(data?.settled)}
          simple={simplePayments}
          symbol={symbol}
        />

        {missingVariants.map((variant) => (
          <div
            key={`${variant.tariff.person}-${variant.tariff.minutes}-${variant.tariff.lang}`}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            <span>
              {t('payments.unpriced')}: {variant.tariff.person} × {t('rewards.minutes', { count: variant.tariff.minutes })}
              {' · '}
              {variant.tariff.lang ? variant.tariff.lang.toUpperCase() : t('payments.foreignTariff')}
              {variant.ids.length > 1 ? ` (${variant.ids.length}×)` : ''}
            </span>
            {canEditPricing && !readOnly ? (
              <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => setAdding(variant)}>
                {t('payments.addTariff')}
              </button>
            ) : null}
          </div>
        ))}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => recalculate.mutate()}
            disabled={readOnly || recalculate.isPending}
            className="rounded-md p-1.5 text-brand-600 hover:bg-brand-50 disabled:opacity-40"
            title={t('payments.recalculate')}
          >
            <RefreshCw size={22} className={recalculate.isPending ? 'animate-spin' : ''} />
          </button>
        </div>

        {isLoading ? (
          <div className="flex h-24 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full border-collapse">
              <tbody>
                {rows.map((fee) => (
                  <FeeRow
                    key={fee.id}
                    fee={fee}
                    reservation={lessons.find((l) => l.id === fee.client_reservation_id) ?? reservation}
                    readOnly={readOnly}
                    symbol={symbol}
                    busy={updateFee.isPending}
                    onSave={(payload) => updateFee.mutate({ id: fee.id, ...payload })}
                    onRemove={() => removeFee.mutate(fee.id)}
                  />
                ))}

                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-400">
                      {t('payments.noItems')}
                    </td>
                  </tr>
                ) : null}

                {!readOnly ? (
                  <tr className="border-t border-slate-200">
                    <td className="w-36 bg-slate-50 px-3 py-2 align-middle">
                      {lessons.length > 1 ? (
                        <LessonPicker lessons={lessons} value={target} onChange={setTarget} />
                      ) : (
                        <TimeCell reservation={reservation} />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className="input"
                        placeholder={t('payments.descriptionPlaceholder')}
                        value={description}
                        maxLength={150}
                        onChange={(e) => setDescription(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && canAdd) {
                            e.preventDefault()
                            addFee.mutate()
                          }
                        }}
                      />
                    </td>
                    <td className="px-1 py-2">
                      <div className="flex items-center gap-1">
                        <input
                          className="input w-24 text-right"
                          type="number"
                          min={0}
                          step="0.01"
                          placeholder="0"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && canAdd) {
                              e.preventDefault()
                              addFee.mutate()
                            }
                          }}
                        />
                        <span className="text-slate-600">{symbol}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="btn-secondary whitespace-nowrap uppercase"
                        disabled={!canAdd || addFee.isPending}
                        onClick={() => addFee.mutate()}
                      >
                        {addFee.isPending ? <Spinner /> : <Plus size={14} />}
                        {t('payments.add')}
                      </button>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>

            <TotalDiscountRow
              discount={discountRow}
              subtotal={subtotal}
              symbol={symbol}
              readOnly={readOnly}
              busy={totalDiscount.isPending}
              scope={
                lessons.length > 1
                  ? t('payments.discountScope', { date: shortDateLong(lessonStart(lessons, target)) })
                  : null
              }
              onApply={(payload) => totalDiscount.mutate(payload)}
            />

            <div className="flex items-center justify-between border-t-2 border-slate-300 px-3 py-2.5">
              <span className="text-base font-bold">{t('global.total')}</span>
              <span className="pr-3 text-base font-bold text-green-700">
                {formatAmount(total)} {symbol}
              </span>
            </div>
          </div>
        )}

        {ref !== null && data && !simplePayments ? (
          <PaymentsSection
            order={data}
            symbol={symbol}
            readOnly={readOnly}
            onDone={invalidate}
            onError={onError}
            clearError={() => setError(null)}
          />
        ) : null}

        {ref !== null && data && !readOnly && !simplePayments && Number.parseFloat(data.refundable) > 0 ? (
          <RefundSection
            order={data}
            symbol={symbol}
            onDone={invalidate}
            onError={onError}
            clearError={() => setError(null)}
          />
        ) : null}

        {ref !== null && data?.receipt.enabled ? (
          <ReceiptSection
            order={data}
            symbol={symbol}
            readOnly={readOnly}
            onDone={invalidate}
            onError={onError}
            clearError={() => setError(null)}
          />
        ) : null}
      </div>
      {adding ? (
        <MissingPriceDialog
          tariff={adding.tariff}
          clientReservationIds={adding.ids}
          onClose={() => setAdding(null)}
        />
      ) : null}
    </Modal>
  )
}

/** Rovnaký chýbajúci variant pri viacerých lekciách objednávky - jedna výzva. */
function groupMissing(rows: Array<MissingTariff & { client_reservation_id: number }>) {
  const groups = new Map<string, { tariff: MissingTariff; ids: number[] }>()
  for (const row of rows) {
    const key = `${row.person}-${row.minutes}-${row.lang}`
    const group = groups.get(key) ?? {
      tariff: { person: row.person, minutes: row.minutes, lang: row.lang },
      ids: [],
    }
    group.ids.push(row.client_reservation_id)
    groups.set(key, group)
  }
  return [...groups.values()]
}

// ---------------------------------------------------------------------------
export function OrderHeader({
  paymentRef,
  lessons,
  total,
  paid,
  due,
  overpaid,
  settled,
  symbol,
  simple,
}: {
  paymentRef: number | null
  lessons: ClientReservation[]
  total: number
  paid: number
  due: number
  overpaid: number
  settled: boolean
  symbol: string
  simple?: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
        <div>
          <div className="text-xs uppercase text-slate-500">{t('reservation.paymentRef')}</div>
          <div className="font-mono text-base font-bold text-slate-800">
            {paymentRef ?? '—'}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase text-slate-500">{t('payments.lessons')}</div>
          <div className="text-sm text-slate-700">
            {lessons.length}
            {lessons.length ? (
              <span className="ml-2 text-slate-500">
                {shortDateLong(lessons[0].start)}
                {lessons.length > 1 ? ` – ${shortDateLong(lessons[lessons.length - 1].start)}` : ''}
              </span>
            ) : null}
          </div>
        </div>

        <div className="ml-auto flex items-center gap-5 text-right">
          <Amount label={t('payments.charged')} value={total} symbol={symbol} />
          {simple ? null : (
            <Amount label={t('payments.received')} value={paid} symbol={symbol} tone="text-green-700" />
          )}
          {simple ? null : overpaid > 0 ? (
            <Amount label={t('payments.overpaid')} value={overpaid} symbol={symbol} tone="text-amber-600" />
          ) : (
            <Amount
              label={t('payments.due')}
              value={due}
              symbol={symbol}
              tone={due > 0 ? 'text-brand-600' : 'text-slate-500'}
            />
          )}
        </div>
      </div>

      {simple ? null : settled ? (
        <div className="mt-1.5 text-xs text-green-700">{t('payments.settledHint')}</div>
      ) : paid > 0 ? (
        <div className="mt-1.5 text-xs text-amber-700">{t('payments.partialHint')}</div>
      ) : null}
    </div>
  )
}

function Amount({
  label,
  value,
  symbol,
  tone = 'text-slate-800',
}: {
  label: string
  value: number
  symbol: string
  tone?: string
}) {
  return (
    <div>
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className={cn('text-base font-bold', tone)}>
        {formatAmount(value)} {symbol}
      </div>
    </div>
  )
}

/**
 * Prijaté platby objednávky.
 *
 * Suma je predvyplnená zostatkom, takže „zaplatil všetko“ je jedno kliknutie
 * a čiastočná úhrada znamená prepísať sumu - nie hľadať iné tlačidlo.
 */
function PaymentsSection({
  order,
  symbol,
  readOnly,
  onDone,
  onError,
  clearError,
}: {
  order: OrderDetail
  symbol: string
  readOnly?: boolean
  onDone: () => void
  onError: (err: unknown) => void
  clearError: () => void
}) {
  const { t } = useTranslation()
  const due = Number.parseFloat(order.due)

  const [amount, setAmount] = useState(due > 0 ? due.toFixed(2) : '')
  const [type, setType] = useState<number>(PaymentType.Cash)
  const [note, setNote] = useState('')
  // UID / číslo dokladu z eKasy školy - len pri hotovosti.
  const [ekasa, setEkasa] = useState('')

  useEffect(() => {
    setAmount(due > 0 ? due.toFixed(2) : '')
  }, [due])

  const pay = useMutation({
    mutationFn: () =>
      ordersApi.pay(order.payment_ref, {
        amount: amount.replace(',', '.'),
        payment_type: type,
        note: note.trim(),
        ...(type === PaymentType.Cash && ekasa.trim() ? { ekasa_ref: ekasa.trim() } : {}),
      }),
    onSuccess: () => {
      setNote('')
      setEkasa('')
      clearError()
      onDone()
    },
    onError,
  })

  const setEkasaRef = useMutation({
    mutationFn: ({ id, value }: { id: number; value: string }) =>
      ordersApi.setEkasaRef(id, value.trim()),
    onSuccess: () => onDone(),
    onError,
  })

  const credit = Number.parseFloat(order.credit_balance || '0')
  const fromCredit = useMutation({
    mutationFn: () => ordersApi.payFromCredit(order.payment_ref),
    onSuccess: () => {
      clearError()
      onDone()
    },
    onError,
  })

  const remove = useMutation({
    mutationFn: (id: number) => ordersApi.removePayment(id),
    onSuccess: () => {
      clearError()
      onDone()
    },
    onError,
  })

  const typed = Number(amount.replace(',', '.'))
  const canPay = !readOnly && due > 0 && typed > 0 && typed <= due + 0.01

  return (
    <div className="overflow-x-auto rounded-md border border-slate-200">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-1.5">
        <span className="text-xs font-medium uppercase text-slate-500">{t('payments.receivedTitle')}</span>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <InvoiceButtons order={order} readOnly={readOnly} />
          <SendDocumentsButton order={order} readOnly={readOnly} />
        </div>
      </div>

      {order.payments.length ? (
        <table className="w-full border-collapse">
          <tbody>
            {order.payments.map((payment) => (
              <PaymentRow
                key={payment.id}
                payment={payment}
                symbol={symbol}
                readOnly={readOnly}
                busy={remove.isPending}
                document={documentOf(order, payment.id)}
                onRemove={() => {
                  if (confirmRemovePayment(t, payment, symbol)) remove.mutate(payment.id)
                }}
                onEditEkasa={() => {
                  const value = window.prompt(t('payments.ekasaRef'), payment.ekasa_ref ?? '')
                  if (value !== null) setEkasaRef.mutate({ id: payment.id, value })
                }}
              />
            ))}
          </tbody>
        </table>
      ) : (
        <div className="px-4 py-4 text-center text-sm text-slate-400">
          {t('payments.noPayments')}
        </div>
      )}

      {/* Uhradená objednávka nemá čo zapísať - namiesto vypnutých (sivých)
          polí len krátka veta; prázdny vypnutý formulár pôsobil ako chyba. */}
      {!readOnly && due <= 0 ? (
        <p className="border-t border-slate-200 px-3 py-2 text-sm text-green-700">
          {t('payments.nothingDue')}
        </p>
      ) : null}
      {!readOnly && due > 0 ? (
        <div className="flex flex-wrap items-end gap-2 border-t border-slate-200 px-3 py-2.5">
          <label className="block">
            <span className="label">{t('payments.amount')}</span>
            <div className="flex items-center gap-1">
              <input
                className="input w-28 text-right"
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <span className="text-slate-600">{symbol}</span>
            </div>
          </label>

          <label className="block">
            <span className="label">{t('payments.method')}</span>
            <Select
              className="w-44"
              value={type}
              onChange={(e) => setType(Number(e.target.value))}
            >
              {PAYMENT_TYPES.map((p) => (
                <option key={p} value={p}>
                  {t(`reservation.payment.${p}`)}
                </option>
              ))}
            </Select>
          </label>

          {type === PaymentType.Cash ? (
            <label className="block">
              <span className="label">{t('payments.ekasaRef')}</span>
              <input
                className="input w-44"
                maxLength={64}
                value={ekasa}
                placeholder={t('payments.ekasaPlaceholder')}
                onChange={(e) => setEkasa(e.target.value)}
              />
            </label>
          ) : null}

          <label className="block min-w-[10rem] flex-1">
            <span className="label">{t('global.note')}</span>
            <input
              className="input"
              maxLength={150}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          <button
            type="button"
            className="btn-primary whitespace-nowrap"
            disabled={!canPay || pay.isPending}
            onClick={() => pay.mutate()}
          >
            {pay.isPending ? <Spinner /> : null}
            {t('payments.record')}
          </button>

          <p className="w-full text-xs text-slate-500">{t('payments.partialAllowed')}</p>
        </div>
      ) : null}

      {!readOnly && due > 0 ? (
        <UnmatchedSuggestions order={order} symbol={symbol} onDone={onDone} onError={onError} />
      ) : null}

      {!readOnly && credit > 0 && due > 0 ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 bg-emerald-50/60 px-3 py-2">
          <span className="text-sm text-emerald-800">
            {t('payments.creditAvailable', { amount: `${trimZeros(order.credit_balance)} ${symbol}` })}
          </span>
          <button
            type="button"
            className="btn-secondary ml-auto"
            disabled={fromCredit.isPending}
            onClick={() => fromCredit.mutate()}
          >
            {fromCredit.isPending ? <Spinner /> : null}
            {t('payments.payFromCredit')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Nespárované platby z importu z banky, ktoré by mohli patriť tejto
 * objednávke - aby ich recepcia priradila rovno odtiaľto, bez hľadania
 * v „Platby → Nespárované“.
 *
 * Ponúkajú sa len tie, ktoré sa zmestia do zostatku (priradenie berie presne
 * sumu z banky a preplatok odmietne). Navrch idú tie, kde sedí meno
 * platiteľa s klientom alebo suma so zostatkom.
 */
function UnmatchedSuggestions({
  order,
  symbol,
  onDone,
  onError,
}: {
  order: OrderDetail
  symbol: string
  onDone: () => void
  onError: (err: unknown) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const settings = useBootstrap().data?.settings
  const allowed = usePlanFeature('bank_import').allowed
  const enabled = allowed && hasBankImports(settings) && !settings?.simple_payments
  const due = Number.parseFloat(order.due)

  const { data } = useQuery({
    queryKey: ['payments-unmatched', 'for-order'],
    queryFn: () => unmatchedApi.list({ limit: 200 }),
    enabled,
  })

  const match = useMutation({
    mutationFn: (id: number) =>
      unmatchedApi.match(id, { ref: order.payment_ref, payment_type: PaymentType.Transfer }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments-unmatched'] })
      void qc.invalidateQueries({ queryKey: ['payments-unmatched-count'] })
      onDone()
    },
    onError,
  })

  if (!enabled) return null
  const surname = (order.client?.full_name ?? '').split(' ').pop()?.toLowerCase() ?? ''
  const score = (row: { counterparty: string; amount: string }) =>
    (surname && row.counterparty.toLowerCase().includes(surname) ? 2 : 0) +
    (Math.abs(Number.parseFloat(row.amount) - due) < 0.01 ? 1 : 0)
  const rows = (data?.results ?? [])
    .filter((row) => {
      const amount = Number.parseFloat(row.amount)
      return amount > 0 && amount <= due + 0.01
    })
    .sort((a, b) => score(b) - score(a) || b.transaction_date.localeCompare(a.transaction_date))
    .slice(0, 5)
  if (!rows.length) return null

  return (
    <div className="border-t border-slate-200 px-3 py-2">
      <div className="mb-1 text-xs font-medium uppercase text-slate-500">
        {t('payments.unmatchedForOrder')}
      </div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={cn(score(row) > 0 && 'bg-emerald-50/60')}>
              <td className="py-1 pr-2 text-slate-600">{shortDateLong(row.transaction_date)}</td>
              <td className="py-1 pr-2">
                <span className="text-slate-700">{row.counterparty || '—'}</span>
                {row.variable_symbol ? (
                  <span className="ml-2 font-mono text-xs text-slate-400">{row.variable_symbol}</span>
                ) : null}
                {row.message ? <span className="ml-2 text-xs text-slate-500">{row.message}</span> : null}
              </td>
              <td className="whitespace-nowrap py-1 pr-2 text-right font-medium">
                {trimZeros(row.amount)} {symbol}
              </td>
              <td className="w-24 py-1 text-right">
                <button
                  type="button"
                  className="btn-secondary px-2 py-0.5 text-xs"
                  disabled={match.isPending}
                  onClick={() => {
                    if (window.confirm(t('payments.unmatchedConfirm', { amount: `${trimZeros(row.amount)} ${symbol}` }))) {
                      match.mutate(row.id)
                    }
                  }}
                >
                  {t('payments.unmatchedAssign')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Vrátenie platby - po zrušení lekcie, alebo keď cena klesla pod zaplatené.
 *
 * Predvolená suma je preplatok (ak nejaký je), inak všetko vrátiteľné.
 * Škola si zvolí, či peniaze vráti, alebo ich pripíše zákazníkovi ako kredit
 * na ďalšiu rezerváciu. Storno poplatok si ponechá tak, že vráti menej.
 */
function RefundSection({
  order,
  symbol,
  onDone,
  onError,
  clearError,
}: {
  order: OrderDetail
  symbol: string
  onDone: () => void
  onError: (err: unknown) => void
  clearError: () => void
}) {
  const { t } = useTranslation()
  const refundable = Number.parseFloat(order.refundable)
  const overpaid = Number.parseFloat(order.overpaid)
  const suggested = overpaid > 0 ? overpaid : refundable

  const [amount, setAmount] = useState(suggested.toFixed(2))
  const [mode, setMode] = useState<'money' | 'credit'>('money')
  const [type, setType] = useState<number>(PaymentType.Cash)
  const [note, setNote] = useState('')

  useEffect(() => {
    setAmount(suggested.toFixed(2))
  }, [suggested])

  const refund = useMutation({
    mutationFn: () =>
      ordersApi.refund(order.payment_ref, {
        amount: amount.replace(',', '.'),
        mode,
        ...(mode === 'money' ? { payment_type: type } : {}),
        note: note.trim(),
      }),
    onSuccess: (result) => {
      setNote('')
      clearError()
      onDone()
      // Doklad o vrátenej platbe (VP) sa tlačí hneď.
      for (const id of result.storno_receipts) {
        window.open(cashReceiptsApi.pdfUrl(id), '_blank', 'noopener')
      }
    },
    onError,
  })

  const typed = Number(amount.replace(',', '.'))
  const valid = typed > 0 && typed <= refundable + 0.01
  const label = `${trimZeros(String(typed))} ${symbol}`

  function submit() {
    const question =
      mode === 'credit'
        ? t('payments.refundConfirmCredit', { amount: label })
        : t('payments.refundConfirmMoney', { amount: label })
    if (window.confirm(question)) refund.mutate()
  }

  return (
    <div className="rounded-md border border-amber-200">
      <div className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium uppercase text-amber-800">
        {t('payments.refundTitle')}
      </div>
      <div className="flex flex-wrap items-end gap-2 px-3 py-2.5">
        <label className="block">
          <span className="label">{t('payments.refundAmount')}</span>
          <div className="flex items-center gap-1">
            <input
              className="input w-28 text-right"
              type="number"
              min={0}
              max={refundable}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <span className="text-slate-600">{symbol}</span>
          </div>
        </label>

        <div className="flex flex-col gap-1 text-sm">
          <label className="flex items-center gap-1.5">
            <input type="radio" checked={mode === 'money'} onChange={() => setMode('money')} />
            {t('payments.refundMoney')}
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              checked={mode === 'credit'}
              disabled={!order.client}
              onChange={() => setMode('credit')}
            />
            {t('payments.refundCredit')}
          </label>
        </div>

        {mode === 'money' ? (
          <label className="block">
            <span className="label">{t('payments.method')}</span>
            <Select
              className="w-44"
              value={type}
              onChange={(e) => setType(Number(e.target.value))}
            >
              {PAYMENT_TYPES.map((p) => (
                <option key={p} value={p}>
                  {t(`reservation.payment.${p}`)}
                </option>
              ))}
            </Select>
          </label>
        ) : null}

        <label className="block min-w-[10rem] flex-1">
          <span className="label">{t('global.note')}</span>
          <input
            className="input"
            maxLength={150}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        <button
          type="button"
          className="btn-secondary whitespace-nowrap"
          disabled={!valid || refund.isPending}
          onClick={submit}
        >
          {refund.isPending ? <Spinner /> : null}
          {t('payments.refundButton')}
        </button>

        {mode === 'money' && order.receipt.enabled ? (
          <p className="w-full text-xs font-medium text-amber-800">{t('payments.refundVp')}</p>
        ) : null}
        <p className="w-full text-xs text-slate-500">{t('payments.refundHint')}</p>
      </div>
    </div>
  )
}

/**
 * Potvrdenie o platbe — vystavuje sa tu, nie na samostatnej obrazovke.
 *
 * Doklad je papier k peniazom, ktoré už prišli, takže patrí tam, kde sa
 * platba eviduje. Predvolene kryje **celú objednávku** (všetky lekcie
 * s tým istým symbolom), a operátor k nej môže pridať staršie lekcie toho
 * istého klienta — zase len tie, na ktorých niečo prišlo.
 */
function ReceiptSection({
  order,
  symbol,
  readOnly,
  onDone,
  onError,
  clearError,
}: {
  order: OrderDetail
  symbol: string
  readOnly?: boolean
  onDone: () => void
  onError: (err: unknown) => void
  clearError: () => void
}) {
  const { t } = useTranslation()
  const receipt = order.receipt
  const available = Number.parseFloat(receipt.available ?? '0')

  // Koľko z ktorej lekcie ešte nie je na doklade. Lekcia bez takej sumy sa
  // pridať nedá - už je zdokladovaná.
  const openByLesson = new Map<number, number>()
  for (const fee of order.fees) {
    if (fee.paid || fee.client_reservation_id === null) continue
    openByLesson.set(
      fee.client_reservation_id,
      (openByLesson.get(fee.client_reservation_id) ?? 0) + Number.parseFloat(fee.amount || '0'),
    )
  }

  const [picked, setPicked] = useState<Set<number>>(
    () => new Set(order.lessons.filter((l) => openByLesson.has(l.id)).map((l) => l.id)),
  )
  const [description, setDescription] = useState('')

  const issue = useMutation({
    mutationFn: () => cashReceiptsApi.issueFor([...picked], description.trim()),
    onSuccess: (created) => {
      clearError()
      setDescription('')
      onDone()
      // Doklad sa tlačí hneď - inak by ho operátor hľadal na inej obrazovke.
      window.open(cashReceiptsApi.pdfUrl(created.id), '_blank', 'noopener')
    },
    onError,
  })

  const candidates = receipt.candidates ?? []
  const selected = [...picked].reduce((sum, id) => {
    const own = openByLesson.get(id)
    if (own !== undefined) return sum + own
    const extra = candidates.find((c) => c.id === id)
    return sum + Number.parseFloat(extra?.amount ?? '0')
  }, 0)

  function toggle(id: number) {
    setPicked((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <div className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium uppercase text-slate-500">
        {t('payments.receiptTitle')}
      </div>

      {receipt.issued?.length ? (
        <div className="flex flex-wrap gap-2 border-b border-slate-200 px-3 py-2">
          {receipt.issued.map((doc) => (
            <a
              key={doc.id}
              href={cashReceiptsApi.pdfUrl(doc.id, doc.storno)}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs',
                doc.storno
                  ? 'border-slate-200 text-slate-400 line-through'
                  : 'border-brand-200 text-brand-700 hover:bg-brand-50',
              )}
            >
              <FileText size={12} />
              {doc.number} · {trimZeros(doc.amount)} {symbol}
            </a>
          ))}
        </div>
      ) : null}

      {!receipt.can_issue ? (
        <div className="px-4 py-3 text-sm text-slate-500">
          {order.payments.length
            ? t('payments.allDocumented')
            : t('payments.receiptNeedsPayment')}
        </div>
      ) : (
        <div className="space-y-2 px-3 py-2.5">
          <p className="text-xs text-slate-500">
            {t('payments.receiptScope', {
              amount: `${formatAmount(available)} ${symbol}`,
              number: receipt.next_number ?? '',
            })}
          </p>

          <div className="space-y-1">
            {order.lessons.map((lesson) => {
              const open = openByLesson.get(lesson.id)
              return (
                <Pick
                  key={lesson.id}
                  label={`${shortDateLong(lesson.start)} ${formatTime(lesson.start)}`}
                  note={
                    open === undefined
                      ? t('payments.alreadyDocumented')
                      : `${formatAmount(open)} ${symbol}`
                  }
                  checked={picked.has(lesson.id)}
                  disabled={readOnly || open === undefined}
                  onChange={() => toggle(lesson.id)}
                />
              )
            })}
          </div>

          {candidates.length ? (
            <div className="space-y-1 border-t border-slate-100 pt-2">
              <div className="text-xs uppercase text-slate-500">
                {t('payments.otherLessons')}
              </div>
              {candidates.map((extra) => (
                <Pick
                  key={extra.id}
                  label={`${shortDateLong(extra.start)} ${formatTime(extra.start)}`}
                  note={`${trimZeros(extra.amount)} ${symbol} · ${extra.payment_ref ?? ''}`}
                  checked={picked.has(extra.id)}
                  disabled={readOnly}
                  onChange={() => toggle(extra.id)}
                />
              ))}
            </div>
          ) : null}

          <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-2">
            <label className="min-w-[12rem] flex-1 text-xs uppercase text-slate-500">
              {t('payments.receiptDescription')}
              <input
                className="input"
                maxLength={100}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>

            <div className="text-right">
              <div className="text-xs uppercase text-slate-500">{t('global.total')}</div>
              <div className="text-base font-bold">
                {formatAmount(selected)} {symbol}
              </div>
            </div>

            <button
              type="button"
              className="btn-primary whitespace-nowrap"
              disabled={
                readOnly || picked.size === 0 || issue.isPending || selected > available + 0.01
              }
              onClick={() => issue.mutate()}
            >
              {issue.isPending ? <Spinner /> : <FileText size={15} />}
              {t('payments.issueReceipt')}
            </button>
          </div>

          {selected > available + 0.01 ? (
            <p className="text-xs text-amber-700">
              {t('payments.receiptTooMuch', {
                amount: `${formatAmount(available)} ${symbol}`,
              })}
            </p>
          ) : null}
        </div>
      )}
    </div>
  )
}

function Pick({
  label,
  note,
  checked,
  disabled,
  onChange,
}: {
  label: string
  note: string
  checked: boolean
  disabled?: boolean
  onChange: () => void
}) {
  return (
    <label
      className={cn(
        'flex items-center gap-2 text-sm',
        disabled ? 'text-slate-400' : 'text-slate-700',
      )}
    >
      <input
        type="checkbox"
        className="text-brand-500 disabled:opacity-40"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span className="font-medium">{label}</span>
      <span className="ml-auto tabular-nums text-slate-500">{note}</span>
    </label>
  )
}

function PaymentRow({
  payment,
  symbol,
  readOnly,
  busy,
  onRemove,
  onEditEkasa,
  document,
}: {
  payment: Payment
  symbol: string
  readOnly?: boolean
  busy: boolean
  onRemove: () => void
  onEditEkasa: () => void
  /** Doklad k platbe (potvrdenie alebo VP). */
  document?: OrderDocument
}) {
  const { t } = useTranslation()
  // Vrátenie platby (peniazmi alebo na kredit) je záporná platba.
  const negative = Number.parseFloat(payment.amount) < 0

  return (
    <tr className="border-b border-slate-200 last:border-b-0 odd:bg-slate-50/60">
      <td className="w-36 px-3 py-2 text-sm text-slate-600">{shortDateLong(payment.created)}</td>
      <td className="px-3 py-2 text-sm">
        <span className="font-medium text-slate-700">
          {negative
            ? payment.payment_type === PaymentType.Credit
              ? t('payments.toCredit')
              : `${t('payments.refunded')} · ${t(`reservation.payment.${payment.payment_type}`)}`
            : t(`reservation.payment.${payment.payment_type}`)}
        </span>
        {payment.note ? <span className="ml-2 text-slate-500">{payment.note}</span> : null}
        {document ? <DocumentLink document={document} /> : null}
        <ProviderFee payment={payment} />
        {payment.from_receipt && !document ? (
          <span className="ml-2 text-xs text-slate-400">{t('payments.fromReceipt')}</span>
        ) : null}
        {payment.ekasa_ref ? (
          <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
            eKasa {payment.ekasa_ref}
          </span>
        ) : null}
        {/* Doplnenie čísla z eKasy, keď sa pri zápise zabudlo (napr. platba
            označená rýchlo z kalendára). */}
        {!readOnly && !negative && payment.payment_type === PaymentType.Cash ? (
          <button
            type="button"
            className="ml-2 text-xs text-action-700 hover:underline"
            onClick={onEditEkasa}
          >
            {payment.ekasa_ref ? t('payments.ekasaEdit') : t('payments.ekasaAdd')}
          </button>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        <span className={cn('text-base font-bold', negative ? 'text-red-600' : 'text-green-700')}>
          {trimZeros(payment.amount)}
        </span>
        <span className="ml-1.5 text-slate-600">{symbol}</span>
      </td>
      <td className="w-10 px-2 py-2 text-right">
        {payment.document ? (
          // Platba s dokladom sa nemaže - zámok vysvetlí, čo urobiť namiesto toho.
          <HelpTip label={t('payments.locked')} icon={<Lock size={14} />} align="right">
            {t(payment.document === 'invoice' ? 'payments.lockedInvoice' : 'payments.lockedReceipt')}
          </HelpTip>
        ) : !readOnly ? (
          <button
            type="button"
            className="rounded p-1 text-slate-400 hover:bg-brand-50 hover:text-brand-600"
            title={t('global.delete')}
            disabled={busy}
            onClick={onRemove}
          >
            <Trash2 size={15} />
          </button>
        ) : null}
      </td>
    </tr>
  )
}

function LessonPicker({
  lessons,
  value,
  onChange,
}: {
  lessons: ClientReservation[]
  value: number
  onChange: (id: number) => void
}) {
  return (
    <Select
      className="w-full text-sm"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      {lessons.map((lesson) => (
        <option key={lesson.id} value={lesson.id}>
          {shortDateLong(lesson.start)} {formatTime(lesson.start)}
        </option>
      ))}
    </Select>
  )
}

// ---------------------------------------------------------------------------
interface RowPayload {
  description?: string
  amount?: string
  discount_type?: DiscountType | null
  discount_value?: string | null
}

function FeeRow({
  fee,
  reservation,
  readOnly,
  symbol,
  busy,
  onSave,
  onRemove,
}: {
  fee: ClientFee
  reservation: ClientReservation
  readOnly?: boolean
  symbol: string
  busy: boolean
  onSave: (payload: RowPayload) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()
  const isBasic = fee.is_basic
  const locked = Boolean(readOnly || fee.paid)

  const [editing, setEditing] = useState(false)
  // The typed amount is always the price BEFORE the discount.
  const gross = fee.gross_amount ?? fee.amount
  const [draft, setDraft] = useState({
    description: fee.description ?? '',
    amount: gross,
    discount_type: fee.discount_type,
    discount_value: fee.discount_value ?? '',
  })

  function open() {
    setDraft({
      description: fee.description ?? '',
      amount: fee.gross_amount ?? fee.amount,
      discount_type: fee.discount_type,
      discount_value: fee.discount_value ?? '',
    })
    setEditing(true)
  }

  function save() {
    onSave({
      description: draft.description,
      amount: String(draft.amount).replace(',', '.'),
      discount_type: draft.discount_type ?? null,
      discount_value: draft.discount_type
        ? String(draft.discount_value).replace(',', '.')
        : null,
    })
    setEditing(false)
  }

  if (editing) {
    return (
      <tr className="border-b border-slate-200 bg-action-50/40">
        <td className="w-36 bg-slate-50 px-3 py-2 align-top">
          <TimeCell reservation={reservation} at={fee.reservation_start} />
        </td>
        <td className="px-3 py-2" colSpan={2}>
          <input
            className="input mb-2"
            value={draft.description}
            maxLength={150}
            placeholder={isBasic ? t('payments.basePrice') : t('payments.descriptionPlaceholder')}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-xs uppercase text-slate-500">{t('payments.gross')}</span>
            <input
              className="input w-24 text-right"
              type="number"
              min={0}
              step="0.01"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
            />
            <span className="text-slate-600">{symbol}</span>

            <span className="ml-2 text-xs uppercase text-slate-500">
              {t('payments.discount')}
            </span>
            <input
              className="input w-20 text-right"
              type="number"
              min={0}
              max={draft.discount_type === 'amount' ? Number(draft.amount) || undefined : 100}
              step="0.01"
              placeholder="0"
              value={draft.discount_value}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  discount_value: e.target.value,
                  discount_type: draft.discount_type ?? 'percent',
                })
              }
            />
            <DiscountTypeToggle
              value={draft.discount_type}
              symbol={symbol}
              onChange={(v) => setDraft({ ...draft, discount_type: v })}
            />
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-right align-top">
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="rounded-md p-1.5 text-green-700 hover:bg-green-50"
              title={t('global.save')}
              disabled={busy}
              onClick={save}
            >
              {busy ? <Spinner /> : <Check size={17} />}
            </button>
            <button
              type="button"
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
              title={t('global.cancel')}
              onClick={() => setEditing(false)}
            >
              <X size={17} />
            </button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr className="group border-b border-slate-200 last:border-b-0 odd:bg-slate-50/60">
      <td className="w-36 bg-slate-50 px-3 py-2 align-middle">
        <TimeCell reservation={reservation} at={fee.reservation_start} />
      </td>
      <td className="px-3 py-2" colSpan={2}>
        <div className="font-semibold text-brand-600">
          {isBasic ? t('payments.basePrice') : fee.description || fee.type_name || '—'}
        </div>
        {isBasic && fee.description ? (
          <div className="text-sm italic text-slate-500">{fee.description}</div>
        ) : null}
        {fee.discount_type ? (
          <div className="text-xs text-action-700">
            {t('payments.discountApplied', {
              value:
                fee.discount_type === 'percent'
                  ? `${trimZeros(fee.discount_value ?? '0')} %`
                  : `${trimZeros(fee.discount_value ?? '0')} ${symbol}`,
              gross: `${trimZeros(fee.gross_amount ?? '0')} ${symbol}`,
            })}
          </div>
        ) : null}
        {fee.paid ? (
          <div className="text-xs text-green-700">
            {t('rewards.paid')}
            {fee.cash_receipt_number ? ` · ${fee.cash_receipt_number}` : ''}
          </div>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right align-middle">
        {fee.gross_amount ? (
          <span className="mr-1.5 text-sm text-slate-400 line-through">
            {trimZeros(fee.gross_amount)}
          </span>
        ) : null}
        <span className="text-base font-bold">{trimZeros(fee.amount)}</span>
        <span className="ml-1.5 text-slate-600">{symbol}</span>

        {!locked ? (
          <span className="ml-2 inline-flex gap-0.5 align-middle opacity-0 transition-opacity group-hover:opacity-100">
            <button
              type="button"
              onClick={open}
              className="rounded p-1 text-slate-500 hover:bg-action-50 hover:text-action-600"
              title={t('global.edit')}
            >
              <Tag size={15} />
            </button>
            {!isBasic ? (
              <button
                type="button"
                onClick={onRemove}
                className="rounded p-1 text-slate-500 hover:bg-brand-50 hover:text-brand-600"
                title={t('global.delete')}
              >
                <Trash2 size={15} />
              </button>
            ) : null}
          </span>
        ) : null}
      </td>
    </tr>
  )
}

function TotalDiscountRow({
  discount,
  subtotal,
  symbol,
  readOnly,
  busy,
  scope,
  onApply,
}: {
  discount: ClientFee | null
  subtotal: number
  symbol: string
  readOnly?: boolean
  busy: boolean
  scope?: string | null
  onApply: (payload: {
    discount_type?: DiscountType | null
    discount_value?: string | null
  }) => void
}) {
  const { t } = useTranslation()
  const [type, setType] = useState<DiscountType>(discount?.discount_type ?? 'percent')
  const [value, setValue] = useState(discount?.discount_value ?? '')

  const locked = Boolean(readOnly || discount?.paid)

  // Preview what the operator is about to save, clamped the same way the
  // backend clamps it - the total can never fall below zero.
  const typed = Number(String(value).replace(',', '.'))
  const cut =
    Number.isNaN(typed) || typed <= 0
      ? 0
      : type === 'percent'
        ? (subtotal * Math.min(typed, 100)) / 100
        : Math.min(typed, subtotal)
  const preview = Math.max(0, subtotal - cut)
  const wouldClamp =
    !Number.isNaN(typed) &&
    typed > 0 &&
    (type === 'percent' ? typed > 100 : typed > subtotal)

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50/60 px-3 py-2">
      <Percent size={15} className="text-slate-400" />
      <span className="text-sm font-medium">{t('payments.totalDiscount')}</span>
      {scope ? <span className="text-xs text-slate-500">{scope}</span> : null}
      <span className="text-xs text-slate-500">
        {t('payments.ofSubtotal', { amount: `${formatAmount(subtotal)} ${symbol}` })}
      </span>

      <div className="ml-auto flex items-center gap-2">
        {locked ? (
          <span className="text-sm font-semibold text-action-700">
            {discount ? `${trimZeros(discount.amount)} ${symbol}` : '—'}
          </span>
        ) : (
          <>
            <input
              className="input w-20 text-right"
              type="number"
              min={0}
              max={type === 'amount' ? subtotal : 100}
              step="0.01"
              placeholder="0"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <DiscountTypeToggle value={type} symbol={symbol} onChange={(v) => setType(v ?? 'percent')} />
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() =>
                onApply(
                  value.trim() === '' || Number(value.replace(',', '.')) <= 0
                    ? { discount_type: null, discount_value: null }
                    : { discount_type: type, discount_value: value.replace(',', '.') },
                )
              }
            >
              {busy ? <Spinner /> : null}
              {t('global.save')}
            </button>
            {discount ? (
              <span className="text-sm font-semibold text-action-700">
                {trimZeros(discount.amount)} {symbol}
              </span>
            ) : null}
          </>
        )}
      </div>

      {!locked && cut > 0 ? (
        <div className="w-full text-right text-xs text-slate-500">
          {t('payments.afterDiscount', {
            amount: `${formatAmount(preview)} ${symbol}`,
          })}
          {wouldClamp ? (
            <span className="ml-2 text-amber-700">{t('payments.discountClamped')}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function DiscountTypeToggle({
  value,
  symbol,
  onChange,
}: {
  value: DiscountType | null | undefined
  symbol: string
  onChange: (v: DiscountType | null) => void
}) {
  const options: Array<{ key: DiscountType; label: string }> = [
    { key: 'percent', label: '%' },
    { key: 'amount', label: symbol },
  ]
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-300">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cn(
            'px-2.5 py-1 text-sm font-medium',
            value === o.key
              ? 'bg-action-500 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-50',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function TimeCell({
  reservation,
  at,
}: {
  reservation: ClientReservation
  at?: string | null
}) {
  return (
    <>
      <div className="text-sm font-bold leading-tight">
        {shortDateLong(at ?? reservation.start)}
      </div>
      <div className="text-sm leading-tight text-slate-600">
        {formatTime(reservation.start)}-{formatTime(reservation.end)}
      </div>
    </>
  )
}

function lessonStart(lessons: ClientReservation[], id: number): string | null {
  return lessons.find((l) => l.id === id)?.start ?? null
}

/** Krátky dátum s mesiacom slovom - vo formáte jazyka aplikácie
 *  („18. 8. 26“ → „18. aug 26“, „18 Aug 26“, „18. Aug. 26“). */
function shortDateLong(value: string | null): string {
  const d = parseNaive(value)
  if (!d) return ''
  return d.toLocaleDateString(getActiveLocale(), { day: 'numeric', month: 'short', year: '2-digit' })
}

/** 25.00 -> "25", 25.50 -> "25,50" (podľa jazyka) - prázdne desatinné
 *  miesta sa vynechávajú ako na pôvodnej obrazovke. */
function trimZeros(value: string): string {
  const n = Number.parseFloat(value || '0')
  return Number.isInteger(n) ? formatNumber(n, { max: 0 }) : formatAmount(n)
}
